/**
 * useAccountHandlers Hook
 * 
 * Handles account CRUD operations (create, edit, delete, archive, unarchive).
 * Manages account form state and dialog state.
 */

import React, { useState, FormEvent, MouseEvent } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  doc,
  writeBatch,
  runTransaction,
  updateDoc,
  serverTimestamp,
  Timestamp,
  deleteField,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Account, AccountGroup, AccountDetailValue } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

export interface FormData {
  accountName: string;
  group: string;
  amount: string;
  currency: string;
  description: string;
  details?: AccountDetailValue;
}

export interface UseAccountHandlersParams {
  workspaceId: string | undefined;
  accounts: Account[];
  accountHasTransactions: Record<string, boolean>;
  formatCurrency: (amount: number, currency: string) => string;
  setSnackbarElement: (element: React.ReactElement | null) => void;
  setSnackbarOpen: (open: boolean) => void;
}

export interface UseAccountHandlersReturn {
  // Form state
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  editingAccount: Account | null;
  setEditingAccount: React.Dispatch<React.SetStateAction<Account | null>>;
  initialFormData: FormData | null;
  setInitialFormData: React.Dispatch<React.SetStateAction<FormData | null>>;
  showAccountDetails: boolean;
  setShowAccountDetails: React.Dispatch<React.SetStateAction<boolean>>;
  initialShowAccountDetails: boolean;
  setInitialShowAccountDetails: React.Dispatch<React.SetStateAction<boolean>>;
  accountNameError: string;
  setAccountNameError: React.Dispatch<React.SetStateAction<string>>;
  isSavingAccount: boolean;
  setIsSavingAccount: React.Dispatch<React.SetStateAction<boolean>>;
  creatingAccountName: string | null;
  setCreatingAccountName: React.Dispatch<React.SetStateAction<string | null>>;
  
  // Dialog state
  createDialogOpen: boolean;
  setCreateDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editDialogOpen: boolean;
  setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deleteConfirmOpen: boolean;
  setDeleteConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Menu state
  menuAnchor: HTMLElement | null;
  setMenuAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  selectedAccount: Account | null;
  setSelectedAccount: React.Dispatch<React.SetStateAction<Account | null>>;
  
  // Handlers
  handleCreateClick: () => void;
  handleMenuOpen: (event: MouseEvent<HTMLElement>, account: Account) => void;
  handleMenuClose: () => void;
  handleEdit: () => void;
  handleDuplicate: () => void;
  handleDeleteClick: () => void;
  handleDeleteConfirm: () => Promise<void>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDialogClose: () => void;
}

export function useAccountHandlers({
  workspaceId,
  accounts,
  accountHasTransactions,
  formatCurrency,
  setSnackbarElement,
  setSnackbarOpen,
}: UseAccountHandlersParams): UseAccountHandlersReturn {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    accountName: "",
    group: "",
    amount: "",
    currency: "USD",
    description: "",
  });
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [initialFormData, setInitialFormData] = useState<FormData | null>(null);
  const [showAccountDetails, setShowAccountDetails] = useState<boolean>(false);
  const [initialShowAccountDetails, setInitialShowAccountDetails] = useState<boolean>(false);
  const [accountNameError, setAccountNameError] = useState<string>("");
  const [isSavingAccount, setIsSavingAccount] = useState<boolean>(false);
  const [creatingAccountName, setCreatingAccountName] = useState<string | null>(null);
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  
  // Handle create account
  const handleCreateClick = (): void => {
    setEditingAccount(null);
    setFormData({
      accountName: "",
      group: "",
      amount: "",
      currency: "USD",
      description: "",
      details: {},
    });
    setShowAccountDetails(false);
    setInitialShowAccountDetails(false);
    setAccountNameError("");
    setIsSavingAccount(false);
    setCreatingAccountName(null);
    setCreateDialogOpen(true);
  };
  
  // Handle menu open
  const handleMenuOpen = (event: MouseEvent<HTMLElement>, account: Account): void => {
    setMenuAnchor(event.currentTarget);
    setSelectedAccount(account);
  };
  
  const handleMenuClose = (): void => {
    setMenuAnchor(null);
    setSelectedAccount(null);
  };
  
  // Handle edit account
  const handleEdit = (): void => {
    if (!selectedAccount) return;
    setEditingAccount(selectedAccount);
    // Check if details field exists (even if empty) - this indicates checkbox was checked
    const hasDetails = selectedAccount.details !== undefined;
    const initialData: FormData = {
      accountName: selectedAccount.accountName || "",
      group: selectedAccount.group || "",
      amount: selectedAccount.amount?.toString() || "",
      currency: selectedAccount.currency || "USD",
      description: selectedAccount.description || "",
      details: selectedAccount.details || {},
    };
    setFormData(initialData);
    setInitialFormData(initialData);
    setShowAccountDetails(hasDetails);
    setInitialShowAccountDetails(hasDetails);
    setAccountNameError("");
    setIsSavingAccount(false);
    setCreatingAccountName(null);
    handleMenuClose();
    setEditDialogOpen(true);
  };
  
  // Handle duplicate account
  const handleDuplicate = (): void => {
    if (!selectedAccount) return;
    setEditingAccount(null);
    setFormData({
      accountName: `${selectedAccount.accountName} (Copy)`,
      group: selectedAccount.group || "",
      amount: selectedAccount.amount?.toString() || "",
      currency: selectedAccount.currency || "USD",
      description: selectedAccount.description || "",
      details: selectedAccount.details ? { ...selectedAccount.details } : {},
    });
    handleMenuClose();
    setCreateDialogOpen(true);
  };
  
  // Handle delete account
  const handleDeleteClick = (): void => {
    setMenuAnchor(null); // Close menu but keep selectedAccount
    setDeleteConfirmOpen(true);
  };
  
  // Confirm and execute account deletion
  const handleDeleteConfirm = async (): Promise<void> => {
    // DEMO MODE: Block all delete operations - demo mode is read-only
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Accounts cannot be deleted. Please log in to delete accounts." />
      );
      setSnackbarOpen(true);
      setDeleteConfirmOpen(false);
      setSelectedAccount(null);
      return;
    }
    
    if (!selectedAccount || !workspaceId || !auth.currentUser) return;
    
    // Double-check that account has no transactions
    if (accountHasTransactions[selectedAccount.id]) {
      alert("Cannot delete account that has been used in transactions. Please archive it instead.");
      setDeleteConfirmOpen(false);
      setSelectedAccount(null);
      return;
    }
    
    try {
      const batch = writeBatch(db);
      const accountRef = doc(db, "workspaces", workspaceId, "accounts", selectedAccount.id);
      
      // Delete the account
      batch.delete(accountRef);
      
      await batch.commit();
      
      setDeleteConfirmOpen(false);
      setSelectedAccount(null);
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account");
    }
  };
  
  // Note: Archive/unarchive logic has been moved to useAccountArchive hook
  
  // Handle create/edit submit
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // In demo mode, accounts cannot be created or edited
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Accounts cannot be created or edited. Please log in to manage accounts." />
      );
      setSnackbarOpen(true);
      setCreateDialogOpen(false);
      setEditDialogOpen(false);
      setFormData({
        accountName: "",
        group: "",
        amount: "",
        currency: "USD",
        description: "",
        details: {},
      });
      setEditingAccount(null);
      setAccountNameError("");
      setIsSavingAccount(false);
      setCreatingAccountName(null);
      return;
    }
    
    // Allow in bypass auth mode (dev workspace allows writes without authentication)
    if (!workspaceId || (!auth.currentUser && !isBypassAuthEnabled())) return;
    
    // Set flag to prevent duplicate check from showing error for the account being created
    setIsSavingAccount(true);
    if (!editingAccount) {
      setCreatingAccountName(formData.accountName.trim());
    }
    
    // Allow empty amount (defaults to 0)
    const amount = formData.amount === "" || formData.amount === undefined ? 0 : parseFloat(formData.amount);
    if (isNaN(amount)) {
      setIsSavingAccount(false);
      setCreatingAccountName(null);
      alert("Please enter a valid amount");
      return;
    }
    
    // Check for duplicate account names (including archived accounts)
    const trimmedName = formData.accountName.trim();
    const duplicateAccount = accounts.find(
      (acc) => acc.accountName.toLowerCase() === trimmedName.toLowerCase() && 
               acc.id !== editingAccount?.id
    );
    
    if (duplicateAccount) {
      const archivedMsg = duplicateAccount.isArchived ? " (archived)" : "";
      setAccountNameError(`Account with name "${duplicateAccount.accountName}"${archivedMsg} already exists. Please use a different name.`);
      setIsSavingAccount(false);
      setCreatingAccountName(null);
      return;
    }
    
    // Clear error if no duplicate - clear immediately to prevent showing error after creation
    setAccountNameError("");
    
    try {
      // Get userId - use placeholder for bypass auth mode
      const userId = auth.currentUser?.uid || (isBypassAuthEnabled() ? "dev-user" : "");
      if (!userId && !isBypassAuthEnabled()) {
        throw new Error("User not authenticated");
      }
      
      const accountData: any = {
        userId: userId,
        accountName: trimmedName,
        group: formData.group as AccountGroup,
        amount: amount,
        currency: formData.currency,
        description: formData.description || "",
        createdAt: editingAccount
          ? editingAccount.createdAt
          : serverTimestamp(),
      };
      
      // Handle details based on checkbox state
      if (editingAccount) {
        // When editing: if checkbox is checked, save details (even if empty); if unchecked, remove them
        if (showAccountDetails) {
          // Save details (even if empty object) to indicate details are enabled
          accountData.details = formData.details || {};
        } else {
          // Checkbox is unchecked - remove details field
          accountData.details = deleteField();
        }
      } else {
        // When creating: only add details if checkbox is checked (even if empty)
        if (showAccountDetails) {
          // Save details (even if empty object) to indicate details are enabled
          accountData.details = formData.details || {};
        }
      }
      
      const accountsRef = collection(db, "workspaces", workspaceId, "accounts");
      const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");

      if (editingAccount) {
        const accountRef = doc(db, "workspaces", workspaceId, "accounts", editingAccount.id);
        const previousAmount = parseFloat(String(editingAccount.amount || 0));
        const newAmount = amount;

        // Preserve order when editing (unless group changed)
        if (editingAccount.group === formData.group) {
          accountData.order = (editingAccount as any).order ?? 0;
        } else {
          const newGroupAccounts = accounts.filter((acc) => acc.group === formData.group && acc.id !== editingAccount.id);
          const maxOrderInNewGroup = newGroupAccounts.length > 0
            ? Math.max(...newGroupAccounts.map((acc) => (acc as any).order ?? 0))
            : -1;
          accountData.order = maxOrderInNewGroup + 1;
        }

        const needsAdjustment = previousAmount !== newAmount;

        if (needsAdjustment) {
          // Allocate user-friendly ID for adjustment (same sequence as other transactions)
          await runTransaction(db, async (transaction) => {
            let seq: number | undefined;
            const settingsSnap = await transaction.get(settingsRef);
            const data = settingsSnap.data();
            const prefix = data?.transactionIdPrefix;
            if (prefix != null && typeof prefix === "string" && prefix.trim() !== "") {
              seq = typeof data?.transactionIdNextSequence === "number" ? data.transactionIdNextSequence : 1;
              transaction.update(settingsRef, { transactionIdNextSequence: seq + 1 });
            }
            transaction.update(accountRef, accountData);
            const adjustmentAmount = newAmount - previousAmount;
            const adjustmentRef = doc(transactionsRef);
            transaction.set(adjustmentRef, {
              userId: userId,
              type: "adjustment",
              amount: adjustmentAmount,
              currency: formData.currency,
              date: Timestamp.fromDate(new Date()),
              note: `Balance adjusted from ${formatCurrency(previousAmount, formData.currency)} to ${formatCurrency(newAmount, formData.currency)}`,
              accountId: editingAccount.id,
              accountName: editingAccount.accountName,
              previousAmount: previousAmount,
              isInitialBalance: false,
              createdAt: serverTimestamp(),
              ...(seq != null ? { friendlyIdSequence: seq } : {}),
            });
          });
        } else {
          const batch = writeBatch(db);
          batch.update(accountRef, accountData);
          await batch.commit();
        }
      } else {
        // Create new account
        const accountsInSameGroup = accounts.filter((acc) => acc.group === formData.group);
        const maxOrder = accountsInSameGroup.length > 0
          ? Math.max(...accountsInSameGroup.map((acc) => (acc as any).order ?? 0))
          : -1;
        accountData.order = maxOrder + 1;

        if (amount !== 0) {
          // Allocate user-friendly ID for initial balance adjustment
          await runTransaction(db, async (transaction) => {
            let seq: number | undefined;
            const settingsSnap = await transaction.get(settingsRef);
            const data = settingsSnap.data();
            const prefix = data?.transactionIdPrefix;
            if (prefix != null && typeof prefix === "string" && prefix.trim() !== "") {
              seq = typeof data?.transactionIdNextSequence === "number" ? data.transactionIdNextSequence : 1;
              transaction.update(settingsRef, { transactionIdNextSequence: seq + 1 });
            }
            const accountRef = doc(accountsRef);
            transaction.set(accountRef, accountData);
            const adjustmentRef = doc(transactionsRef);
            transaction.set(adjustmentRef, {
              userId: userId,
              type: "adjustment",
              amount: amount,
              currency: formData.currency,
              date: Timestamp.fromDate(new Date()),
              note: `Initial balance for account "${formData.accountName}"`,
              accountId: accountRef.id,
              accountName: formData.accountName,
              isInitialBalance: true,
              createdAt: serverTimestamp(),
              ...(seq != null ? { friendlyIdSequence: seq } : {}),
            });
          });
        } else {
          const batch = writeBatch(db);
          const accountRef = doc(accountsRef);
          batch.set(accountRef, accountData);
          await batch.commit();
        }
      }
      
      setCreateDialogOpen(false);
      setEditDialogOpen(false);
      setFormData({
        accountName: "",
        group: "",
        amount: "",
        currency: "USD",
        description: "",
        details: {},
      });
      setEditingAccount(null);
      setAccountNameError("");
      setIsSavingAccount(false);
      setCreatingAccountName(null);
    } catch (error) {
      console.error("Error saving account:", error);
      alert("Failed to save account");
      setIsSavingAccount(false);
      setCreatingAccountName(null);
    }
  };
  
  const handleDialogClose = (): void => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setFormData({
      accountName: "",
      group: "",
      amount: "",
      currency: "USD",
      description: "",
      details: {},
    });
    setInitialFormData(null);
    setShowAccountDetails(false);
    setInitialShowAccountDetails(false);
    setEditingAccount(null);
    setAccountNameError("");
    setIsSavingAccount(false);
    setCreatingAccountName(null);
  };
  
  return {
    // Form state
    formData,
    setFormData,
    editingAccount,
    setEditingAccount,
    initialFormData,
    setInitialFormData,
    showAccountDetails,
    setShowAccountDetails,
    initialShowAccountDetails,
    setInitialShowAccountDetails,
    accountNameError,
    setAccountNameError,
    isSavingAccount,
    setIsSavingAccount,
    creatingAccountName,
    setCreatingAccountName,
    
    // Dialog state
    createDialogOpen,
    setCreateDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    
    // Menu state
    menuAnchor,
    setMenuAnchor,
    selectedAccount,
    setSelectedAccount,
    
    // Handlers
    handleCreateClick,
    handleMenuOpen,
    handleMenuClose,
    handleEdit,
    handleDuplicate,
    handleDeleteClick,
    handleDeleteConfirm,
    handleSubmit,
    handleDialogClose,
  };
}
